import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button as MaterialButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Checkbox,
  FormHelperText,
  OutlinedInput,
  Select,
  TextField,
  InputAdornment,
  useTheme,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';

import { DataGrid, GridOverlay, GridToolbarContainer } from '@mui/x-data-grid';
import JSZip from 'jszip';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { Auth } from 'aws-amplify';
import UserInfoDrawer from "../Overlays/UserInfoDrawer";
import { requestFeedback, getAllFeedbacksByGroup, getUsersByGroup, getDoorSign } from "../../services/UserService";
import formatDate from "../../utils/formatDate";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const activities = {
  'Ri': 'Hvordan var det å ri på hest i dag?',
  'Klatring': 'Hvordan var det å klatre i dag?',
  'Basseng': 'Hvordan var det å være i bassenget i dag?',
  'Aktivitet gymsal': 'Hvordan var det med aktivitet i gymsal i dag?',
  'Sykling': 'Hvordan var det å sykle i dag?',
  'Være på tur': 'Hvordan var det å være på tur i dag?',
  'Friluftsliv': 'Hvordan var det å drive med friluftsliv i dag?',
  'Alpin': 'Hvordan var det å drive med alpint i dag?',
  'Skøyter': 'Hvordan var det å skøyte i dag?',
  'Langrenn': 'Hvordan var det å drive med langrenn i dag?',
  'Utendørs vann': 'Hvordan var det å drive med vannaktivitet ute i dag?',
  'Aktivitet skjerm': 'Hvordan var det å drive med aktivitet på skjerm i dag?',
  'Leke ute': 'Hvordan var det å leke ute i dag?',
  'Bevegelse musikk': 'Hvordan var det å bevege seg til musikk i dag?'
}

const activityMapping = {
  'Ri': 'horse',
  'Klatring': 'climbing',
  'Basseng': 'pool',
  'Aktivitet gymsal': 'gym',
  'Sykling': 'bicycle',
  'Være på tur': 'hike',
  'Friluftsliv': 'outdoor',
  'Alpin': 'alpine',
  'Skøyter': 'skates',
  'Langrenn': 'skiing',
  'Utendørs vann': 'canoe',
  'Aktivitet skjerm': 'screen',
  'Leke ute': 'playOutside',
  'Bevegelse musikk': 'musicMove'
};

const activityFields = [
  'horse', 'climbing', 'pool', 'gym', 'cycle', 'hike', 'outdoor',
  'alpin', 'skates', 'skiing', 'canoe', 'screen', 'playOutside', 'musicMove'
];
const summerActivityFields = activityFields.filter(field => !['skates', 'alpin', 'skiing'].includes(field));
const winterActivityFields = activityFields.filter(field => !['cycle', 'canoe'].includes(field));

const activityFieldAliases = {
  cycle: ['cycle', 'bicycle'],
  alpin: ['alpin', 'alpine'],
};

const getRequiredActivities = (user) => {
  const season = String(user.isWinter ?? user.season ?? '').toLowerCase();
  const isWinter = user.isWinter === true || user.isWinter === 1 ||
    season === 'true' || season === '1' || season === 'vinter' || season === 'winter';

  return isWinter ? winterActivityFields : summerActivityFields;
};

const isWithinDepartureWindow = (user) => {
  if (!user.departureDate) return true;

  const departureDate = new Date(user.departureDate);
  if (Number.isNaN(departureDate.getTime())) return true;

  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - 3);

  return departureDate >= cutoffDate;
};

const hasActivityAnswer = (user, field) => {
  const fields = activityFieldAliases[field] || [field];
  return fields.some(activityField => Number(user[activityField]) >= 1 && Number(user[activityField]) <= 5);
};

const generalColumns = [
  {
    field: 'userId',
    headerName: 'PasientID',
    width: 110,
    headerClassName: 'strong-column-header',
    cellClassName: 'strong-column-cell',
  },
  {
    field: 'firstName',
    headerName: 'Fornavn',
    width: 180,
    headerClassName: 'strong-column-header',
    cellClassName: 'strong-column-cell',
    renderCell: ({ row, value }) => {
      const initial = value ? value.charAt(0).toUpperCase() : '';
      const { bgcolor, color } = getAvatarColors(row);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor, color, fontWeight: 700, fontSize: 14 }}>
            {initial}
          </Avatar>
          <Box component="span" sx={{ fontWeight: 600 }}>{value}</Box>
        </Box>
      );
    },
  },
  {
    field: 'arrivalDate',
    headerName: 'Ankomst',
    type: 'dateTime',
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
    headerClassName: 'strong-column-header',
    cellClassName: 'strong-column-cell',
  },
  {
    field: 'departureDate',
    headerName: 'Avreise',
    type: 'dateTime',
    width: 110,
    valueGetter: ({ row }) => row.departureDate,
    valueFormatter: ({ value }) => value ? formatDate(value) : '',
    headerClassName: 'strong-column-header divider-column',
    cellClassName: 'strong-column-cell divider-column',
  },
  ...[
    { field: 'horse', label: 'Ri' },
    { field: 'climbing', label: 'Klatring' },
    { field: 'pool', label: 'Basseng' },
    { field: 'gym', label: 'Aktivitet gymsal' },
    { field: 'cycle', label: 'Sykling' },
    { field: 'hike', label: 'Være på tur' },
    { field: 'outdoor', label: 'Friluftsliv' },
    { field: 'alpin', label: 'Alpin' },
    { field: 'skates', label: 'Skøyter' },
    { field: 'skiing', label: 'Langrenn' },
    { field: 'canoe', label: 'Utendørs vann' },
    { field: 'screen', label: 'Aktivitet skjerm' },
    { field: 'playOutside', label: 'Leke ute' },
    { field: 'musicMove', label: 'Bevegelse musikk' }
  ].map(activity => ({
    field: activity.field,
    headerName: activity.label,
    width: 110,
    valueGetter: ({ row }) => toText(row[activity.field]),
    renderCell: ({ row }) => {
      const rawValue = Number(row[activity.field]);
      const label = toText(rawValue);
      if (!label) return null;
      const colors = chipColors[rawValue] || { bgcolor: '#F1F5F9', color: '#334155' };
      return (
        <Chip
          label={label}
          sx={{
            bgcolor: colors.bgcolor,
            color: colors.color,
            borderRadius: '999px',
            height: 28,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        />
      );
    },
  }))
];

const columnsWithResponses = (users) => generalColumns.filter(column =>
  !activityMapping[column.headerName] || users.some(user => user[column.field] >= 1 && user[column.field] <= 5)
);

const toText = (value) => {
  switch (Number(value)) {
    case 1: return 'Ikke gøy';
    case 2: return 'Skummelt';
    case 3: return 'Litt gøy';
    case 4: return 'Veldig gøy';
    case 5: return 'Vet ikke';
    default: return '';
  }
};

// Maps activity values to their chip colors
const chipColors = {
  1: { bgcolor: '#FDECEC', color: '#D75B6C' }, // Not fun
  2: { bgcolor: '#FCE7D9', color: '#C96A2D' }, // Scary
  3: { bgcolor: '#EDF3FA', color: '#4B6F95' }, // A little fun
  4: { bgcolor: '#EAF7E3', color: '#3F7F4C' }, // Very fun
  5: { bgcolor: '#FFF4D6', color: '#B7791F' }, // Don't know
};

const avatarPalette = ['#DBEAFE', '#DCFCE7'];
const avatarTextPalette = ['#1D4ED8', '#166534'];

const getAvatarColors = (user) => {
  const seed = String(user?.userId || user?.firstName || '').split('').reduce(
    (total, character) => total + character.charCodeAt(0),
    0
  );
  const avatarColors = Object.values(chipColors);

  if (avatarColors.length) {
    return avatarColors[seed % avatarColors.length];
  }

  const index = seed % avatarPalette.length;
  return { bgcolor: avatarPalette[index], color: avatarTextPalette[index] };
};

const TableSearchToolbar = ({ searchValue, onSearchChange, activityFilter, onActivityFilterChange, onResetFilters }) => (
  <GridToolbarContainer sx={{ padding: '12px 16px', gap: 1.5 }}>
    <TextField
      size="small"
      value={searchValue}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder="Søk på ID eller navn"
      inputProps={{ 'aria-label': 'Søk på ID eller navn' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      sx={{
        width: 545,
        '& .MuiOutlinedInput-root': {
          height: 40,
          borderRadius: '10px',
          '& fieldset': {
            borderColor: '#E5E7EB',
          },
          '&:hover fieldset': {
            borderColor: '#CBD5E1',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#CBD5E1',
          },
        },
        '& .MuiInputBase-input': {
          fontSize: 14,
        },
      }}
    />
    <Select
      size="small"
      multiple
      value={activityFilter}
      onChange={(event) => {
        const selectedActivities = event.target.value;
        onActivityFilterChange(selectedActivities.includes('__all__') ? [] : selectedActivities);
      }}
      displayEmpty
      renderValue={(selected) => selected.length ? selected.join(', ') : 'Alle aktiviteter'}
      inputProps={{ 'aria-label': 'Filtrer på aktivitet' }}
      sx={{
        width: 240,
        height: 40,
        marginLeft: 'auto',
        borderRadius: '10px',
        fontSize: 14,
        fontWeight: 600,
        color: '#334155',
        '& .MuiSelect-select': {
          padding: '9px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#E5E7EB',
        },
      }}
    >
      <MenuItem value="__all__">
        <Checkbox checked={!activityFilter.length} />
        Alle aktiviteter
      </MenuItem>
      {Object.keys(activities).map((activity) => (
        <MenuItem key={activity} value={activity}>
          <Checkbox checked={activityFilter.includes(activity)} />
          {activity}
        </MenuItem>
      ))}
    </Select>
    <MaterialButton
      variant="outlined"
      onClick={onResetFilters}
      sx={{
        height: 40,
        minWidth: 130,
        borderRadius: '10px',
        borderColor: '#E5E7EB',
        color: '#334155',
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          borderColor: '#CBD5E1',
          bgcolor: '#F8FAFC',
        },
      }}
    >
      Nullstill filtre
    </MaterialButton>
  </GridToolbarContainer>
);

const NoSearchResultsOverlay = ({ searchValue, onResetFilters }) => (
  <GridOverlay>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#64748B', fontSize: 14 }}>
      <Box component="span">
        {searchValue.trim() ? `Fant ingen brukere for «${searchValue}»` : 'Ingen brukere tilgjengelig'}
      </Box>
      {searchValue.trim() && (
        <MaterialButton onClick={onResetFilters} sx={{ textTransform: 'none' }}>
          Nullstill filtre
        </MaterialButton>
      )}
    </Box>
  </GridOverlay>
);

function getStyles(activity, activityName, theme) {
  return {
    fontWeight: activityName.indexOf(activity) === -1
      ? theme.typography.fontWeightRegular
      : theme.typography.fontWeightMedium,
  };
}

const UsersDataGrid = ({ groupNr }) => {
  const [data, setData] = useState([]);
  const [shownData, setShownData] = useState([]);
  const [user, setUser] = useState([]);
  const [value, setValue] = useState(1);
  const [drawer, setDrawer] = useState(false);
  const [open, setOpen] = useState(false);
  const [activityName, setActivityName] = useState('');
  const [instColumns, setInstColumns] = useState(generalColumns);
  const [instID, setInstID] = useState('01');
  const theme = useTheme();
  const [activityQuestion, setActivityQuestion] = useState('');
  const [error, setError] = useState(false);
  const [currentArrivalDate, setCurrentArrivalDate] = useState(null);
  const [signedInUser, setSignedInUser] = useState(null);
  const [todayFeedbacks, setTodayFeedbacks] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [activityFilter, setActivityFilter] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFetchingDoorSigns, setIsFetchingDoorSigns] = useState(false);

  const resetFilters = () => {
    setSearchValue('');
    setActivityFilter([]);
  };

  useEffect(() => {
    (async () => {
      const user = await Auth.currentAuthenticatedUser();
      const inst = user.attributes.zoneinfo;
      setInstColumns(generalColumns);
      setInstID(inst);
      setUsers(groupNr);
      setSignedInUser(user);
      getFeedbacksByGroup();
    })();
  }, [groupNr]);

  const setUsers = async (groupNr) => {
    setIsLoadingUsers(true);

    try {
      const result = await getUsersByGroup(groupNr)

      if (!result || !result.Items) {
        return
      }

      const recentUsers = result.Items.filter(isWithinDepartureWindow);

      setShownData(recentUsers);

      const currentUsers = recentUsers.filter(isCurrentUser);

      setData(currentUsers);
      setInstColumns(columnsWithResponses(currentUsers));

      if (currentUsers.length > 0) {
        const arrivalDate = new Date(currentUsers[0].arrivalDate).toLocaleDateString();
        setCurrentArrivalDate(arrivalDate);
      } else {
        setCurrentArrivalDate(null);
      }
    } finally {
      setIsLoadingUsers(false);
    }
  };


  // Call this after fetching feedbacks
  const getFeedbacksByGroup = async () => {
    try {
      const feedbackData = await getAllFeedbacksByGroup(groupNr, instID);
      console.log("Fetched Feedback Data:", feedbackData); // Debugging line

      if (feedbackData) {
        const fetchedFeedbacks = feedbackData.feedbacks || [];

        console.log("Fetched Feedbacks:", fetchedFeedbacks);

        // Transform fetched feedbacks to include nested feedback values
        const transformedFeedbacks = fetchedFeedbacks.map(feedback => ({
          ...feedback,
          feedbackValues: feedback.feedbackValues || [], // Ensure feedbackValues is an array
        }));

        const filteredTodayFeedbacks = filterFeedbacksByToday(transformedFeedbacks);
        console.log("Filtered Today's Feedbacks:", filteredTodayFeedbacks); // Debugging line

        setTodayFeedbacks(filteredTodayFeedbacks);

        if (!filteredTodayFeedbacks.some(feedback => feedback.activity === activityMapping[activityName])) {
          setActivityName(''); // Reset if current activityName is invalid
        }
      } else {
        console.error("No feedback data received."); // Error handling
      }
    } catch (error) {
      console.error("Error fetching feedbacks:", error); // Error handling
    }

  };


  const filterFeedbacksByToday = (feedbacks) => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const endOfToday = startOfToday + 24 * 60 * 60 * 1000;

    return feedbacks.filter(feedback => {
      return feedback.timeOfCreation >= startOfToday && feedback.timeOfCreation < endOfToday;
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) filterUsersByCondition(isPreviousUser);
    else if (newValue === 1) filterUsersByCondition(isCurrentUser);
    else filterUsersByCondition(isFutureUser);
  };
  const handleSelectChange = (event) => {
    const { value } = event.target;
    setActivityName(value);
    setActivityQuestion(activities[value]);
    setError(false);
  };

  const filterUsersByCondition = (condition) => {
    const filteredUsers = shownData.filter(condition);
    setData(filteredUsers);
    setInstColumns(columnsWithResponses(filteredUsers));
  };

  const statusFilteredData = data.filter((row) => {
    const requiredActivities = getRequiredActivities(row);
    const completed = requiredActivities.every((field) => hasActivityAnswer(row, field));
    const started = requiredActivities.some((field) => hasActivityAnswer(row, field));

    if (statusFilter === 'completed') return completed;
    if (statusFilter === 'started') return started && !completed;
    if (statusFilter === 'no-answer') return !started;
    return true;
  });

  const searchableData = statusFilteredData.filter((row) => {
    const search = searchValue.trim().toLowerCase();
    return !search || String(row.userId).toLowerCase().includes(search) ||
      String(row.firstName || '').toLowerCase().includes(search);
  });

  const visibleColumns = instColumns.filter((column) =>
    !activityMapping[column.headerName] || !activityFilter.length || activityFilter.includes(column.headerName)
  );

  const showLoadingState = isLoadingUsers;

  const completedActivityUsers = data.filter((row) => {
    return getRequiredActivities(row).every((field) => hasActivityAnswer(row, field));
  }).length;

  const startedActivityUsers = data.filter((row) => {
    const requiredActivities = getRequiredActivities(row);
    const hasStarted = requiredActivities.some((field) => hasActivityAnswer(row, field));
    const hasCompleted = requiredActivities.every((field) => hasActivityAnswer(row, field));

    return hasStarted && !hasCompleted;
  }).length;

  const noAnswerUsers = data.length - completedActivityUsers - startedActivityUsers;

  const isPreviousUser = (user) => new Date(user.departureDate).getTime() <= new Date().getTime();
  const isCurrentUser = (user) => new Date(user.arrivalDate).getTime() <= new Date().getTime() &&
    new Date(user.departureDate).getTime() > new Date().getTime();
  const isFutureUser = (user) => new Date(user.arrivalDate).getTime() > new Date().getTime();

  const handleRowClick = (event) => {
    setDrawer(true);
    setUser(event.row);
  };
  const handleFetchAllDoorSigns = async () => {
    setIsFetchingDoorSigns(true);

    const rows = searchableData;
    const zip = new JSZip();
    let addedCount = 0;

    try {
      // Fetch one at a time - firing them all at once makes the API return 404s
      for (let i = 0; i < rows.length; i++) {
        const result = await getDoorSign(rows[i].userId);

        if (!result || !result.ok || !result.url) continue;

        try {
          const response = await fetch(result.url);
          const blob = await response.blob();
          const extension = blob.type.split('/')[1] || 'png';

          zip.file(`dorskilt-${rows[i].userId}.${extension}`, blob);
          addedCount += 1;
        } catch (fetchError) {
          // CORS not configured on the S3 bucket yet - fall back to opening it in a tab
          console.error(`Could not download door sign for ${rows[i].userId}:`, fetchError);
          window.open(result.url, '_blank', 'noopener');
        }
      }

      if (addedCount > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const arrivalDateLabel = formatDate(rows[0]?.arrivalDate).replace(/\./g, '-');

        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = arrivalDateLabel ? `dorskilt-${groupNr}-${arrivalDateLabel}.zip` : `dorskilt-${groupNr}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(zipUrl);
      }
    } finally {
      setIsFetchingDoorSigns(false);
    }
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    if (!activityName) {
      setError(true);
      return;
    } else {
      setError(false);
      setOpen(false);

      const user = signedInUser;

      const activityField = activityMapping[activityName];

      let feedback_object = {
        instId: user.attributes.zoneinfo,
        arrivalDate: currentArrivalDate,
        groupId: groupNr,
        question: activityQuestion,
        activity: activityField,
        timeOfCreation: new Date().getTime()
      };

      // Save the feedback
      await requestFeedback(feedback_object);
    }
  };

  const handleQuestionChange = (event) => {
    setActivityQuestion(event.target.value);
  }

  return (
    <Box sx={{ flexDirection: 'column', width: '100%', height: '100%', minHeight: 0, display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ flexDirection: 'column', width: '100%', height: '100%', minHeight: 0, display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ borderBottom: '1px solid #E5E7EB', bgcolor: '#FFFFFF', width: '100%', height: 44, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingLeft: 1, paddingRight: 2 }}>
          <Tabs
            value={value}
            onChange={handleChange}
            sx={{
              minHeight: 30,
              '& .MuiTabs-indicator': {
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: 30,
                minWidth: 105,
                padding: '0 10px',
                color: '#64748B',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
              },
              '& .Mui-selected': {
                color: '#00B6CD',
                fontWeight: 700,
              },
            }}
          >
            <Tab label="Tidligere" />
            <Tab label="Nåværende" />
            <Tab label="Kommende" />
          </Tabs>
          {value !== 0 && (
            <MaterialButton
              onClick={handleFetchAllDoorSigns}
              disabled={isFetchingDoorSigns || searchableData.length === 0}
              variant="contained"
              size="small"
              startIcon={!isFetchingDoorSigns && <BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                marginBottom: 1,
                minWidth: 160,
                height: 32,
                borderRadius: '10px',
                fontSize: 12,
                bgcolor: '#BDECF3',
                color: '#087D9A',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#A9E3EC', boxShadow: 'none' },
              }}
            >
              {isFetchingDoorSigns ? <CircularProgress size={16} sx={{ color: '#087D9A' }} /> : 'Hent alle dørskilt'}
            </MaterialButton>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, width: '100%', paddingTop: 2, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, paddingBottom: 2 }}>
              {[
                { label: 'Alle brukere', value: data.length, icon: GroupIcon, color: '#2563EB', background: '#EFF6FF', border: '#DBEAFE' },
                { label: 'Fullført', value: completedActivityUsers, icon: CheckCircleIcon, color: '#16A34A', background: '#F0FDF4', border: '#DCFCE7' },
                { label: 'Påbegynt', value: startedActivityUsers, icon: AccessTimeIcon, color: '#D97706', background: '#FFFBEB', border: '#FEF3C7' },
                { label: 'Ingen svar', value: noAnswerUsers, icon: ErrorIcon, color: '#DC2626', background: '#FFF1F2', border: '#FEE2E2' },
              ].map(({ label, value, icon: Icon, color, background, border }, index) => {
                const filter = ['all', 'completed', 'started', 'no-answer'][index];
                const isActive = statusFilter === filter;

                return (
                  <Box
                    key={label}
                    component="button"
                    type="button"
                    onClick={() => setStatusFilter(filter)}
                    sx={{
                      minHeight: 72,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 1,
                      padding: '8px 12px',
                      bgcolor: background,
                      border: `1px solid ${border}`,
                      borderRadius: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      outline: isActive ? `2px solid ${color}` : 'none',
                      outlineOffset: isActive ? 1 : 0,
                      '&:hover': { filter: 'brightness(0.98)' },
                    }}
                  >
                    <Box sx={{ width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: `${border}99`, color }}>
                      <Icon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', textAlign: 'left' }}>
                      <Box component="div" sx={{ fontSize: 22, lineHeight: 1, fontWeight: 700, color: '#0F172A' }}>{value}</Box>
                      <Box component="div" sx={{ marginTop: 0.25, fontSize: 12.5, fontWeight: 600, color: '#64748B' }}>{label}</Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '16px', overflow: 'hidden' }}>
              {showLoadingState ? (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={30} sx={{ color: '#23ABC7' }} />
                </Box>
              ) : (
                <DataGrid
                  getRowId={(row) => row.userId} // Use appropriate user ID field
                  onRowClick={handleRowClick}
                  rows={searchableData} // Original data for user display
                  columns={visibleColumns}
                  autoPageSize
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  checkboxSelection
                  rowHeight={52}
                  headerHeight={42}
                  getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'}
                  components={{ Toolbar: TableSearchToolbar, NoRowsOverlay: NoSearchResultsOverlay }}
                  componentsProps={{
                    toolbar: {
                      searchValue,
                      onSearchChange: setSearchValue,
                      activityFilter,
                      onActivityFilterChange: setActivityFilter,
                      onResetFilters: resetFilters,
                    },
                    noRowsOverlay: {
                      searchValue,
                      onResetFilters: resetFilters,
                    },
                  }}
                  sx={{
                    bgcolor: '#FFFFFF',
                    border: 'none',
                    flex: 1,
                    minHeight: 0,
                    boxShadow: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#F8FAFC',
                      borderBottom: '1px solid #E5E7EB',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#334155',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: '1px solid #F1F5F9',
                      borderRight: 'none',
                    },
                    '& .strong-column-header .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 700,
                    },
                    '& .strong-column-cell': {
                      fontWeight: 600,
                    },
                    '& .divider-column': {
                      borderRight: '1px solid #E5E7EB',
                    },
                    '& .even-row': {
                      bgcolor: '#FFFFFF',
                    },
                    '& .odd-row': {
                      bgcolor: '#F8FAFC',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#F1F5F9',
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      bgcolor: '#F8FAFC',
                      borderTop: '1px solid #E5E7EB',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box >

      <UserInfoDrawer open={drawer} setOpen={setDrawer} user={user} instID={instID} />

      {/* Dialog for creating new feedback */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Ny evaluering</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Velg aktiviteten du vil brukerene skal evaluere.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FormControl fullWidth sx={{ my: 2, minWidth: 440 }} required error={error}>
              <InputLabel id="multiple-activity-label">Aktivitet</InputLabel>
              <Select
                labelId="multiple-activity-label"
                value={activityName || ''}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Aktivitet" />}
                MenuProps={MenuProps}
              >
                {Object.keys(activities).filter(key => {
                  // Exclude activities that exist in today's feedbacks
                  return !todayFeedbacks.some(feedback => feedback.activity === activityMapping[key]);
                }).map((key) => (
                  <MenuItem key={key} value={key} style={getStyles(key, activityName, theme)}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>Vennligst velg en aktivitet</FormHelperText>} {/* Error message */}
            </FormControl>

            <TextField
              fullWidth
              sx={{ my: 2 }}
              value={activityQuestion}
              onChange={handleQuestionChange}
              autoFocus
              margin="dense"
              label="Spørsmål"
              type="text"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <MaterialButton onClick={handleClose}>Avbryt</MaterialButton>
          <MaterialButton onClick={handleSave}>Send</MaterialButton>
        </DialogActions>
      </Dialog>
    </Box >
  );

};

export default UsersDataGrid;